import type { DataProvider } from "ra-core";
import type { Deal, Task } from "@/components/atomic-crm/types";
import {
  buildStageDatePatch,
  dueDateFromOffset,
  getStageTaskTemplates,
} from "./arkaStageTasks";

type StageDataProvider = Pick<DataProvider, "create" | "getList" | "update">;

export async function applyDealStageEffects(
  dataProvider: StageDataProvider,
  deal: Deal,
  nextStage: string,
): Promise<void> {
  const datePatch = buildStageDatePatch(deal, nextStage);
  const mergedDeal: Deal = { ...deal, stage: nextStage, ...datePatch };

  if (Object.keys(datePatch).length > 0) {
    await dataProvider.update("deals", {
      id: deal.id,
      data: datePatch,
      previousData: deal,
    });
  }

  const templates = getStageTaskTemplates(nextStage, mergedDeal);
  if (templates.length === 0) {
    return;
  }

  const { data: existing = [] } = await dataProvider.getList<Task>("tasks", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "id", order: "ASC" },
    filter: { deal_id: deal.id },
  });
  const existingKeys = new Set(
    existing
      .map((task) => task.auto_key)
      .filter((key): key is string => typeof key === "string" && key.length > 0),
  );

  const contactId = deal.contact_ids[0];
  await Promise.all(
    templates
      .filter((template) => !existingKeys.has(template.autoKey))
      .map((template) =>
        dataProvider.create("tasks", {
          data: {
            deal_id: deal.id,
            contact_id: contactId,
            type: template.type,
            text: template.text,
            due_date: dueDateFromOffset(template.dueOffsetDays),
            sales_id: deal.sales_id,
            auto_key: template.autoKey,
          },
        }),
      ),
  );
}
